import { Alert, Grid, Link, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader from '../components/elements/Loader';
import ResetPasswordForm from '../components/reset-password';
import { useNotification } from '../hooks/NotificationContext';
import AuthWrapper from '../layout/auth/auth-wrapper';
import { resetPassword, setUserAccount, signOut } from '../modules/users';

function ResetPasswordPage() {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [showResponse, setShowResponse] = useState(false);
  const location = useLocation();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  // Extract the token and setup flag from location state
  const { userEmail, isSetup } = location.state || {};
  const isLoggedIn = localStorage.getItem('isLoggedIn');

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get(
      'reset_password_token'
    );
    if (token) setResetToken(token);
  }, []);

  const handleButtonClick = async () => {
    navigate('/login', { replace: true });
  };

  const handleSubmit = async (values) => {
    setError(null);
    setLoading(true);

    try {
      const user = {
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
      };

      if (!isSetup) {
        // For resetting password
        // log out if already logged in
        if (isLoggedIn === 'true') {
          await signOut();
          localStorage.setItem('isLoggedIn', false);
          localStorage.clear();
        }
        await resetPassword({ ...user, resetPasswordToken: resetToken });
      } else {
        // For setting up a new password (invited user)
        await setUserAccount({ ...user, email: userEmail, token: resetToken });
        addNotification('success', 'Your account has been setup successfully!');
      }
      setShowResponse(true);
    } catch (err) {
      setError(err.message || 'Failed to set password');
    }
    setLoading(false);
  };

  return (
    <AuthWrapper>
      {loading ? (
        <Loader />
      ) : (
        <Grid container spacing={2}>
          {showResponse ? (
            <>
              <Grid item xs={12}>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ mb: { xs: -0.5, sm: 0.5 } }}>
                  <Typography variant="h4" gutterBottom color="success.main">
                    {isSetup
                      ? 'Account Setup Completed!'
                      : 'Password Reset Successful!'}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Your account has been setup successfully. You can now log in
                    with your new credentials.
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <Link
                  variant="body2"
                  underline="none"
                  component="button"
                  onClick={handleButtonClick}
                  color="text.primary">
                  <span>&#8592; Go to login</span>
                </Link>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ mb: { xs: -0.5, sm: 0.5 } }}>
                  <Typography variant="h4" gutterBottom>
                    Create a New Password
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                <ResetPasswordForm onSubmit={handleSubmit} />
              </Grid>
            </>
          )}
        </Grid>
      )}
    </AuthWrapper>
  );
}

export default ResetPasswordPage;
